import React from 'react'
import BookkeepingCompleteJobDialogViewModel from './BookkeepingCompleteJobDialogViewModel'
import { observer } from 'mobx-react'
import { useBrandingSettings } from 'hooks/branding'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import ErrorBlock from 'components/ErrorBlock'
import JobViewModel from '../JobViewModel'
import Button from 'components/Button'
import {
  NotificationBar,
  NotificationBarSubtitle,
  NotificationBarTitle,
} from 'components/NotificationBar'

interface BookkeepingCompleteJobDialogProps {
  vm: BookkeepingCompleteJobDialogViewModel
  jobVM: JobViewModel
}

export default observer(function BookkeepingCompleteJobDialog({
  vm,
  jobVM,
}: BookkeepingCompleteJobDialogProps) {
  const { corporateMode } = useBrandingSettings()

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={require('assets/images/img_service-items-complete.svg')}
              alt="Illustration of a checklist"
            />
          )}
          <ModalSheetContentTitle>
            All service items are complete
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            If you finished the last service item, please proceed to complete
            this job.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      {vm.submit.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.submit.error}
          onCloseClick={() => vm.submit.reset()}
        />
      )}
      <PendingUpdatesNotificationBar vm={jobVM} />
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} disabled={vm.submit.pending}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={vm.submit}
            data-test="complete-bookkeeping-dialog-close-job-button"
            loading={vm.submit.pending}
            disabled={jobVM.isScopeChangeRequestPending}
          >
            Complete Job
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface PendingUpdatesNotificationBarProps {
  vm: JobViewModel
}

const PendingUpdatesNotificationBar = observer(
  ({ vm }: PendingUpdatesNotificationBarProps) => {
    if (!vm.isScopeChangeRequestPending) {
      return
    }

    return (
      <NotificationBar centered type="error">
        <NotificationBarTitle>Pending updates:</NotificationBarTitle>
        <NotificationBarSubtitle>
          Unable to close due to pending job scope changes
        </NotificationBarSubtitle>
      </NotificationBar>
    )
  },
)
